import { plainPreview } from "../preview";
import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { readByHuman, type ChatMessage, type PairingClient } from "../protocol";
import type { GroupEntry } from "./useGroups";

import { loadGroupReads as loadReads, saveGroupReads as saveReads } from "../groupReads";

/** Per-group summary for the sidebar: an unread badge count + a last-message preview. Unread means the PEER
 *  (agent) messages the observer hasn't read, read state being their own entry in the message's
 *  per-participant receipts map — a SHARED signal, so reading a group on the phone clears the badge here too.
 *  The local watermark is kept only as a floor for messages that predate receipts, which would otherwise all
 *  resurface as unread. Opening a group (activeKey) stamps those receipts and advances the watermark. */
export interface GroupSummary {
  unread: number;
  preview: string;
  ts: number;
}

/**
 * The peer messages to STAMP with the observer's receipt when they open the group — everything from another
 * participant they haven't receipted yet, deliberately ignoring the local watermark: the receipt is the
 * cross-device signal and must be written even for messages this device already counted as read locally.
 */
export function groupMessagesToStamp(msgs: ChatMessage[]): ChatMessage[] {
  return msgs.filter((m) => m.role === "agent" && !readByHuman(m));
}

/**
 * The badge count, which unlike the stamp list DOES respect the local watermark. Messages that predate
 * receipts carry none, so counting purely by receipt would resurrect the entire pre-receipt history as
 * unread. Collapsing these two into one filter is the tempting simplification that breaks that.
 */
export function groupUnreadCount(msgs: ChatMessage[], watermark: number): number {
  return groupUnread(msgs, watermark).length;
}

/** The unread peer messages themselves — the badge counts these, and the thread rules off above the first,
 *  so the two can't point at different places. Same watermark caveat as the count. */
export function groupUnread(msgs: ChatMessage[], watermark: number): ChatMessage[] {
  return groupMessagesToStamp(msgs).filter((m) => m.ts > watermark);
}

/** Where a group thread should open and show its "Unread" rule: the oldest peer message the observer hasn't
 *  read. Mirrors `firstUnreadId` for a 1:1 chat, over the group's receipts-plus-watermark model. */
export function firstGroupUnreadId(msgs: ChatMessage[], watermark: number): string | null {
  return groupUnread(msgs, watermark)[0]?.id ?? null;
}

export function useGroupUnreads(
  clients: MutableRefObject<Map<string, PairingClient>>,
  groups: GroupEntry[],
  activeKey: string | null,
): { summaries: Record<string, GroupSummary>; unreadFromId: string | null } {
  const [summaries, setSummaries] = useState<Record<string, GroupSummary>>({});
  // Where the open group's "Unread" rule goes. Captured once, at the moment the group is opened and BEFORE
  // the marking below advances the watermark — the same reason the 1:1 anchor is captured off its first
  // snapshot: opening a thread is what marks it read, so anything recomputed afterwards has nothing left to
  // point at. Carries its group's key so a stale anchor can't be drawn over the next group opened.
  const [unreadFrom, setUnreadFrom] = useState<{ key: string; id: string | null } | null>(null);
  const capturedFor = useRef<string | null>(null);
  const reads = useRef<Record<string, number>>(loadReads());
  // Latest peer-message ts seen per group — so opening a group can advance its watermark to "all read".
  const latest = useRef<Record<string, number>>({});
  // The peer messages still lacking the observer's receipt — what opening the group stamps read.
  const unreadMsgs = useRef<Record<string, ChatMessage[]>>({});

  const groupKeys = groups.map((g) => `${g.pairingId}/${g.group.id}`).join(",");
  useEffect(() => {
    const unsubs: Array<() => void> = [];
    for (const g of groups) {
      const key = `${g.pairingId}/${g.group.id}`;
      const client = clients.current.get(g.pairingId);
      if (!client) continue;
      unsubs.push(
        client.subscribeGroupMessages(g.group.id, (msgs) => {
          const peer = msgs.filter((m) => m.role === "agent");
          latest.current[key] = peer.length ? peer[peer.length - 1].ts : 0;
          unreadMsgs.current[key] = groupMessagesToStamp(msgs);
          const count = groupUnreadCount(msgs, reads.current[key] ?? 0);
          const last = msgs[msgs.length - 1];
          const preview = last ? (last.att && !last.text ? "📷 Photo" : plainPreview(last.text)) : "";
          const ts = last?.ts ?? 0;
          setSummaries((u) => (u[key]?.unread === count && u[key]?.preview === preview ? u : { ...u, [key]: { unread: count, preview, ts } }));
        }, 30),
      );
    }
    return () => unsubs.forEach((u) => u?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKeys]);

  // Opening a group marks it read: stamps the observer's receipt on the peer messages (the cross-device
  // signal) and advances this device's watermark to cover anything older than receipts.
  useEffect(() => {
    if (!activeKey) return;
    if (capturedFor.current !== activeKey) {
      capturedFor.current = activeKey;
      setUnreadFrom({ key: activeKey, id: firstGroupUnreadId(unreadMsgs.current[activeKey] ?? [], reads.current[activeKey] ?? 0) });
    }
    const mark = () => {
      reads.current[activeKey] = Math.max(reads.current[activeKey] ?? 0, latest.current[activeKey] ?? Date.now());
      saveReads(reads.current);
      const pending = unreadMsgs.current[activeKey];
      if (pending?.length) {
        const [pairingId, groupId] = [activeKey.slice(0, activeKey.indexOf("/")), activeKey.slice(activeKey.indexOf("/") + 1)];
        void clients.current.get(pairingId)?.markGroupMessagesRead(groupId, pending);
        unreadMsgs.current[activeKey] = [];
      }
      setSummaries((u) => (u[activeKey]?.unread ? { ...u, [activeKey]: { ...u[activeKey], unread: 0 } } : u));
    };
    mark();
    // Re-mark as new messages land while the group stays open.
    const t = setInterval(mark, 1500);
    return () => clearInterval(t);
  }, [activeKey, summaries[activeKey ?? ""]?.unread]);

  return { summaries, unreadFromId: unreadFrom && unreadFrom.key === activeKey ? unreadFrom.id : null };
}
